import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config';
import './BuscadorHistorial.css';

function BuscadorHistorial() {
  const [filtros, setFiltros] = useState({ 
    nombreCompleto: '', 
    documento: '', 
    numeroSocio: '',
    fechaDesde: '',
    fechaHasta: ''
  });
  const [resultados, setResultados] = useState([]);
  const [error, setError] = useState('');
  const [paginacion, setPaginacion] = useState({
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
    itemsPerPage: 10
  });
  const [paginaActual, setPaginaActual] = useState(1);
  const navigate = useNavigate();

  const handleChange = e => {
    setFiltros({ ...filtros, [e.target.name]: e.target.value });
  };

  // Función para formatear JSON de manera amigable
  const formatearValor = (valor) => {
    if (!valor) return 'Sin valor';
    
    try {
      // Intentar parsear como JSON
      const jsonObj = JSON.parse(valor);
      
      // Si es un objeto, formatearlo de manera legible
      if (typeof jsonObj === 'object' && jsonObj !== null) {
        return Object.entries(jsonObj)
          .map(([key, value]) => {
            // Capitalizar la primera letra de la clave
            const keyFormateada = key.charAt(0).toUpperCase() + key.slice(1);
            
            // Formatear fechas si el valor parece ser una fecha
            let valorFormateado = value;
            if (value && typeof value === 'string') {
              // Verificar si es una fecha válida con formato más estricto
              if (esFechaValida(value)) {
                const fecha = new Date(value);
                valorFormateado = fecha.toLocaleDateString('es-ES', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit'
                });
              }
            }
            
            return `${keyFormateada}: ${valorFormateado || 'Sin valor'}`;
          })
          .join('\n');
      }
      
      // Si no es un objeto, verificar si es una fecha
      if (typeof valor === 'string') {
        if (esFechaValida(valor)) {
          const fecha = new Date(valor);
          return fecha.toLocaleDateString('es-ES', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          });
        }
      }
      
      // Si no es un objeto ni fecha, devolver el valor tal como está
      return valor;
    } catch (error) {
      // Si no es JSON válido, verificar si es una fecha
      if (typeof valor === 'string') {
        if (esFechaValida(valor)) {
          const fecha = new Date(valor);
          return fecha.toLocaleDateString('es-ES', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          });
        }
      }
      
      // Si no es JSON válido ni fecha, devolver el valor original
      return valor;
    }
  };

  // Función para verificar si un string es realmente una fecha
  const esFechaValida = (valor) => {
    if (!valor || typeof valor !== 'string') return false;
    
    // Patrones de fecha comunes
    const patronesFecha = [
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/, // ISO datetime
      /^\d{4}-\d{2}-\d{2}$/, // ISO date
      /^\d{2}\/\d{2}\/\d{4}$/, // DD/MM/YYYY
      /^\d{2}-\d{2}-\d{4}$/, // DD-MM-YYYY
      /^\d{4}\/\d{2}\/\d{2}$/, // YYYY/MM/DD
    ];
    
    // Verificar si coincide con algún patrón de fecha
    const esPatronFecha = patronesFecha.some(patron => patron.test(valor));
    
    if (!esPatronFecha) return false;
    
    // Verificar que realmente sea una fecha válida
    const fecha = new Date(valor);
    return !isNaN(fecha.getTime()) && fecha.getFullYear() > 1900 && fecha.getFullYear() < 2100;
  };

  // Función para mostrar solo los campos modificados
  const formatearValorModificado = (valorAnterior, valorNuevo, esValorAnterior = true) => {
    if (!valorAnterior || !valorNuevo) {
      return formatearValor(esValorAnterior ? valorAnterior : valorNuevo);
    }

    try {
      const anterior = JSON.parse(valorAnterior);
      const nuevo = JSON.parse(valorNuevo);
      
      if (typeof anterior === 'object' && typeof nuevo === 'object') {
        const camposModificados = [];
        
        // Encontrar campos que cambiaron
        const todosLosCampos = new Set([...Object.keys(anterior), ...Object.keys(nuevo)]);
        
        todosLosCampos.forEach(campo => {
          const valorAnt = anterior[campo];
          const valorNue = nuevo[campo];
          
          // Solo incluir si el valor cambió
          if (valorAnt !== valorNue) {
            const valorAMostrar = esValorAnterior ? valorAnt : valorNue;
            const keyFormateada = campo.charAt(0).toUpperCase() + campo.slice(1);
            
            // Formatear fechas si el valor parece ser una fecha
            let valorFormateado = valorAMostrar;
            if (valorAMostrar && typeof valorAMostrar === 'string') {
              if (esFechaValida(valorAMostrar)) {
                const fecha = new Date(valorAMostrar);
                valorFormateado = fecha.toLocaleDateString('es-ES', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit'
                });
              }
            }
            
            camposModificados.push(`${keyFormateada}: ${valorFormateado || 'Sin valor'}`);
          }
        });
        
        return camposModificados.join('\n');
      }
      
      // Si no son objetos, usar la función original
      return formatearValor(esValorAnterior ? valorAnterior : valorNuevo);
    } catch (error) {
      // Si no es JSON válido, usar la función original
      return formatearValor(esValorAnterior ? valorAnterior : valorNuevo);
    }
  };

  const handleBuscar = async (e, pagina = 1) => {
    if (e) e.preventDefault();
    setError('');
    setPaginaActual(pagina);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      Object.entries(filtros).forEach(([k, v]) => { if (v) params.append(k, v); });
      params.append('page', pagina);
      params.append('limit', 10);
      const res = await fetch(`${API_ENDPOINTS.HISTORIAL}?${params.toString()}`, {
        headers: { 'Authorization': 'Bearer ' + token }
      });
      const data = await res.json();
      if (res.ok) {
        setResultados(data.historial || []);
        setPaginacion(data.pagination || {
          currentPage: 1,
          totalPages: 0,
          totalItems: 0,
          itemsPerPage: 10
        });
      } else {
        setError(data.message || 'Error en la búsqueda');
      }
    } catch (err) {
      setError('Error de red');
    }
  };

  const handleCambiarPagina = (nuevaPagina) => {
    handleBuscar(null, nuevaPagina);
  };

  return (
    <div className="panel-socio-bg">
      <div className="buscador-historial-card">
        <h2 className="panel-socio-title">Buscador de Historial</h2>
        
        <form onSubmit={handleBuscar} className="panel-socio-form">
          <div className="form-filtros">
            <div className="form-field-group">
              <label htmlFor="nombreCompleto">Nombre Completo</label>
              <input 
                id="nombreCompleto"
                name="nombreCompleto" 
                placeholder="Ingrese nombre completo" 
                value={filtros.nombreCompleto} 
                onChange={handleChange} 
              />
            </div>
            
            <div className="form-field-group">
              <label htmlFor="documento">Documento</label>
              <input 
                id="documento"
                name="documento" 
                placeholder="Ingrese documento" 
                value={filtros.documento} 
                onChange={handleChange} 
              />
            </div>
            
            <div className="form-field-group">
              <label htmlFor="numeroSocio">Número de Socio</label>
              <input 
                id="numeroSocio"
                name="numeroSocio" 
                placeholder="Ingrese número de socio" 
                value={filtros.numeroSocio} 
                onChange={handleChange} 
              />
            </div>
            
            <div className="form-field-group">
              <label htmlFor="fechaDesde">Fecha Desde</label>
              <input 
                id="fechaDesde"
                name="fechaDesde" 
                type="date"
                value={filtros.fechaDesde} 
                onChange={handleChange} 
              />
            </div>
            
            <div className="form-field-group">
              <label htmlFor="fechaHasta">Fecha Hasta</label>
              <input 
                id="fechaHasta"
                name="fechaHasta" 
                type="date"
                value={filtros.fechaHasta} 
                onChange={handleChange} 
              />
            </div>
          </div>
          
          <button type="submit" className="btn-buscar">Buscar Historial</button>
        </form>
        
        {error && <div className="panel-socio-error">{error}</div>}
        
        {resultados.length > 0 ? (
          <div className="tabla-container">
            <table className="resultados-tabla">
              <thead>
                <tr>
                  <th>Número de Socio</th>
                  <th>Campos Modificados</th>
                  <th>Valor Anterior</th>
                  <th>Valor Nuevo</th>
                  <th>Usuario</th>
                  <th>Fecha</th>
                </tr>
              </thead>
              <tbody>
                {resultados.map((h, i) => (
                  <tr key={i}>
                    <td>{h.socio?.numeroSocio || h.socioId}</td>
                    <td>{h.camposModificados}</td>
                    <td>
                      <div className="campo-valor">
                        {formatearValorModificado(h.valorAnterior, h.valorNuevo, true)}
                      </div>
                    </td>
                    <td>
                      <div className="campo-valor">
                        {formatearValorModificado(h.valorAnterior, h.valorNuevo, false)}
                      </div>
                    </td>
                    <td>{h.usuarioNombre} ({h.usuarioDocumento})</td>
                    <td>{new Date(h.createdAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : resultados.length === 0 && !error ? (
          <div className="sin-resultados">
            No se encontraron registros de historial. Realice una búsqueda para ver resultados.
          </div>
        ) : null}
        
        {/* Paginación */}
        {paginacion.totalPages > 1 && (
          <div className="paginacion">
            <div className="paginacion-info">
              Mostrando {((paginacion.currentPage - 1) * paginacion.itemsPerPage) + 1} - {Math.min(paginacion.currentPage * paginacion.itemsPerPage, paginacion.totalItems)} de {paginacion.totalItems} resultados
            </div>
            <div className="paginacion-controles">
              <button 
                className="btn-pagina"
                disabled={paginacion.currentPage === 1}
                onClick={() => handleCambiarPagina(paginacion.currentPage - 1)}
              >
                Anterior
              </button>
              
              {Array.from({ length: Math.min(5, paginacion.totalPages) }, (_, i) => {
                const pagina = i + 1;
                return (
                  <button
                    key={pagina}
                    className={`btn-pagina ${paginacion.currentPage === pagina ? 'activa' : ''}`}
                    onClick={() => handleCambiarPagina(pagina)}
                  >
                    {pagina}
                  </button>
                );
              })}
              
              {paginacion.totalPages > 5 && (
                <>
                  {paginacion.currentPage > 3 && <span className="paginacion-ellipsis">...</span>}
                  <button
                    className={`btn-pagina ${paginacion.currentPage === paginacion.totalPages ? 'activa' : ''}`}
                    onClick={() => handleCambiarPagina(paginacion.totalPages)}
                  >
                    {paginacion.totalPages}
                  </button>
                </>
              )}
              
              <button 
                className="btn-pagina"
                disabled={paginacion.currentPage === paginacion.totalPages}
                onClick={() => handleCambiarPagina(paginacion.currentPage + 1)}
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
        
        <div className="panel-socio-botones">
          <button 
            className="btn-panel-socio btn-accion"
            onClick={() => navigate('/admin')}
          >
            Volver al Panel
          </button>
        </div>
      </div>
    </div>
  );
}

export default BuscadorHistorial; 